/**
 * White-label runtime — derives a firm's full colour system from a primary +
 * accent hex and paints it onto the live app by overriding the brand CSS
 * variables on the document root.
 *
 * ## Why this module exists
 *
 * `TenantBrandingDoc` stores only `primaryColor` + `accentColor` (two hexes),
 * but the UI consumes the full 11-stop `--color-primary-*` / `--color-accent-*`
 * ramps (and the `--color-brand-*` tokens) via the Option-B alias layer in
 * `styles/design-system.css`. So the ramps must be DERIVED at runtime.
 *
 * ## Approach (no dependency, pure colour math)
 *
 * The firm's HUE + SATURATION are transplanted onto Elreign's contrast-proven
 * per-stop LIGHTNESS skeleton (measured from the existing teal/brass ramps via
 * OKLab). OKLab lightness tracks WCAG relative luminance far better than HSL,
 * so inheriting the proven lightness structure keeps the same AA guarantees the
 * hand-tuned ramp has — whatever hue the firm picks. A per-stop AA pass then
 * enforces the load-bearing contrasts numerically as a safety net.
 *
 * The firm's exact hue is preserved; their literal lightness yields to the
 * Elreign skeleton so the surface relationships (light tints stay light, dark
 * shades stay dark) and readability survive any input.
 *
 * ## ⚠ CHROMA IS A PROFILE, NOT A CEILING (fixed 2026-08-23)
 *
 * This module used to cap each stop at `Math.min(firmChroma, skeletonChroma)`.
 * Elreign's own teal and brass are DELIBERATELY desaturated — teal-600 sits at
 * OKLab chroma ~0.05 — so that `min` silently threw away most of the colour any
 * saturated firm picked. A firm choosing `#C0392B` (a plain red, chroma 0.174)
 * got `#5d3933` as `--color-primary-600`: right hue, 70% of the chroma gone,
 * and a dark low-chroma red is BROWN. That is exactly what a real firm saw on a
 * prod walkthrough: they chose red and green, every derived surface came out
 * brown, and the one screen that reads the RAW `--brand-primary` showed the true
 * red — which is what made it look like some screens were branded and others
 * were not.
 *
 * The skeleton's chroma is therefore read as a SHAPE relative to its own anchor
 * stop (the stop that IS the reference brand colour: teal-600, brass-400), and
 * the firm's chroma is scaled through that shape. Elreign's own inputs are an
 * identity transform (firm chroma == anchor chroma → every stop reproduces
 * exactly), while a saturated input keeps its saturation. Anything that falls
 * outside sRGB at the skeleton's lightness is gamut-mapped by reducing chroma at
 * CONSTANT hue + lightness (`oklchToHex`), so the hue family always survives.
 *
 * Everything here is framework-free + side-effect-free except the explicit
 * `*Root` / cache helpers, so it can run synchronously at boot (pre-render) to
 * minimise a flash of default colours.
 */

import { FONT_MAP } from './branding-fonts';

export const RAMP_STOPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
export type RampStop = (typeof RAMP_STOPS)[number];

export interface BrandingColours {
  primaryColor?: string | null;
  accentColor?: string | null;
  /** Firm typography choice — a key into FONT_MAP (branding-fonts.ts). Unknown
   *  / absent leaves the baked Elreign default. */
  fontPair?: string | null;
}

// Font CSS vars the app consumes (design-system.css aliases + index.css @theme).
// headingStack drives the serif/editorial display face; bodyStack the sans body.
const HEADING_FONT_VARS = ['--serif', '--font-editorial'] as const;
const BODY_FONT_VARS = ['--sans', '--font-body', '--font-display', '--font-num'] as const;

export interface Oklch {
  /** Perceptual lightness 0..1. */
  l: number;
  /** Chroma (>= 0). */
  c: number;
  /** Hue in degrees 0..360. */
  h: number;
}

export type Ramp = Record<RampStop, string>;

export interface DerivedRamps {
  teal: Ramp;
  brass: Ramp;
  brand: {
    primary: string;
    primarySoft: string;
    primaryDeep: string;
    accent: string;
    accentSoft: string;
    onPrimary: string;
  };
}

// ── Reference ramps (index.css @theme) — the contrast-proven skeletons ────────
// The firm palette inherits these stops' LIGHTNESS + chroma envelope.
const REF_TEAL: Record<RampStop, string> = {
  50: '#F4F8F7', 100: '#E8F0EE', 200: '#CADDD8', 300: '#A4C2BB', 400: '#6F9F95',
  500: '#3F7468', 600: '#1F4D45', 700: '#143832', 800: '#0E2924', 900: '#091B17', 950: '#04100D',
};
const REF_BRASS: Record<RampStop, string> = {
  50: '#FBF6EC', 100: '#F1E8D6', 200: '#E5D2A8', 300: '#D5B981', 400: '#C19A5B',
  500: '#A37D40', 600: '#84632F', 700: '#664B22', 800: '#4A3618', 900: '#30220F', 950: '#1A1208',
};

// Surfaces / text colours that the load-bearing contrast pairs land on (from
// design-system.css). Worst case for dark-on-light text is the DARKEST light
// surface (--surface-2).
const SURFACE_2 = '#efe9da';

// ── sRGB <-> OKLab (Björn Ottosson) ──────────────────────────────────────────
function srgbToLinear(x: number): number {
  return x >= 0.04045 ? Math.pow((x + 0.055) / 1.055, 2.4) : x / 12.92;
}
function linearToSrgb(x: number): number {
  return x >= 0.0031308 ? 1.055 * Math.pow(x, 1 / 2.4) - 0.055 : 12.92 * x;
}
function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255,
  ];
}
function rgbToHex(r: number, g: number, b: number): string {
  const to = (x: number) =>
    Math.round(clamp01(x) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

function linearRgbToOklab(r: number, g: number, b: number): [number, number, number] {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);
  return [
    0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  ];
}
function oklabToLinearRgb(L: number, a: number, bb: number): [number, number, number] {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * bb;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * bb;
  const s_ = L - 0.0894841775 * a - 1.291485548 * bb;
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

export function hexToOklch(hex: string): Oklch {
  const [r, g, b] = hexToRgb(hex);
  const [L, a, bb] = linearRgbToOklab(srgbToLinear(r), srgbToLinear(g), srgbToLinear(b));
  const c = Math.sqrt(a * a + bb * bb);
  let h = (Math.atan2(bb, a) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { l: L, c, h };
}

/** True when the OKLCh colour maps inside the sRGB gamut (small tolerance). */
function oklchInGamut(l: number, c: number, h: number): boolean {
  const rad = (h * Math.PI) / 180;
  const [r, g, b] = oklabToLinearRgb(l, c * Math.cos(rad), c * Math.sin(rad));
  const eps = 1e-4;
  return (
    r >= -eps && r <= 1 + eps && g >= -eps && g <= 1 + eps && b >= -eps && b <= 1 + eps
  );
}

export function oklchToHex({ l, c, h }: Oklch): string {
  const L = clamp01(l);
  // Gamut-map by reducing chroma at constant L + H (preserves hue) so we never
  // hue-shift via naive channel clamping.
  let lo = 0;
  let hi = Math.max(0, c);
  if (!oklchInGamut(L, hi, h)) {
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (oklchInGamut(L, mid, h)) lo = mid;
      else hi = mid;
    }
  } else {
    lo = hi;
  }
  const rad = (h * Math.PI) / 180;
  const [lr, lg, lb] = oklabToLinearRgb(L, lo * Math.cos(rad), lo * Math.sin(rad));
  return rgbToHex(linearToSrgb(clamp01(lr)), linearToSrgb(clamp01(lg)), linearToSrgb(clamp01(lb)));
}

// ── WCAG 2.2 contrast (for the AA enforcement safety net) ─────────────────────
function wcagLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}
function wcagContrast(fg: string, bg: string): number {
  const a = wcagLuminance(fg);
  const b = wcagLuminance(bg);
  const hi = Math.max(a, b);
  const lo = Math.min(a, b);
  return (hi + 0.05) / (lo + 0.05);
}

// ── Ramp derivation ───────────────────────────────────────────────────────────

interface SkeletonStop {
  stop: RampStop;
  l: number;
  c: number;
}

function skeletonOf(ref: Record<RampStop, string>): SkeletonStop[] {
  return RAMP_STOPS.map((stop) => {
    const o = hexToOklch(ref[stop]);
    return { stop, l: o.l, c: o.c };
  });
}
const TEAL_SKELETON = skeletonOf(REF_TEAL);
const BRASS_SKELETON = skeletonOf(REF_BRASS);

/** Lowest OKLab L (>= floor) for hue `h`/chroma `c` whose colour, used as TEXT,
 *  clears `target`:1 on `bg`. Darkening only raises dark-on-light contrast. */
function lowerLForTextContrast(l: number, c: number, h: number, bg: string, target: number): number {
  let L = l;
  for (let i = 0; i < 60 && L > 0.04; i++) {
    if (wcagContrast(oklchToHex({ l: L, c, h }), bg) >= target) break;
    L -= 0.01;
  }
  return L;
}
/** Lowest OKLab L whose colour, used as a BACKGROUND, clears `target`:1 under
 *  light `text`. Darkening the bg raises contrast vs light text. */
function lowerLForBgContrast(l: number, c: number, h: number, text: string, target: number): number {
  let L = l;
  for (let i = 0; i < 60 && L > 0.04; i++) {
    if (wcagContrast(text, oklchToHex({ l: L, c, h })) >= target) break;
    L -= 0.01;
  }
  return L;
}

function onPrimaryFor(src: Oklch): string {
  // A near-white tint of the firm hue — cohesive cream that stays high-contrast
  // on the (always-dark) primary button.
  return oklchToHex({ l: 0.95, c: Math.min(src.c, 0.03), h: src.h });
}

interface RampOptions {
  /**
   * The stop that IS the reference brand colour for this ramp — teal-600 is
   * `--color-brand-primary`, brass-400 is `--color-brand-accent`. It anchors the
   * chroma profile: the firm's own chroma lands here, every other stop is scaled
   * by how that stop's chroma relates to this one in the reference ramp.
   */
  anchorStop: RampStop;
  /** Which stop is dark TEXT on light surfaces (teal:600, brass:600). */
  textStop: RampStop;
  /** Which stop is a dark BACKGROUND under light text (teal:700). */
  bgStop?: RampStop;
  /** Light text colour used when enforcing the bgStop contrast. */
  bgText?: string;
  /** Stop used as dark text on the `pairLightStop` background (brass:700 on 100). */
  pairStop?: RampStop;
  pairLightStop?: RampStop;
}

/**
 * Derive an 11-stop ramp: the firm hue/chroma on the reference lightness
 * skeleton, then enforce the load-bearing AA pairs, then clamp strictly
 * monotonic (descending lightness).
 */
function deriveRamp(sourceHex: string, skeleton: SkeletonStop[], opts: RampOptions): Ramp {
  const src = hexToOklch(sourceHex);
  // Step 1: hue transplant onto the reference L skeleton, with the firm's chroma
  // scaled through the skeleton's chroma PROFILE (see the module docstring —
  // taking `min` here is what turned a chosen red into brown).
  const anchorChroma = skeleton.find((s) => s.stop === opts.anchorStop)?.c ?? 1;
  const L: Record<RampStop, number> = {} as Record<RampStop, number>;
  const C: Record<RampStop, number> = {} as Record<RampStop, number>;
  for (const s of skeleton) {
    L[s.stop] = s.l;
    // ratio 1.0 at the anchor → the firm's own saturation renders there; the
    // tints/shades keep the reference ramp's relative fall-off. Out-of-gamut
    // combinations are reduced at constant hue by oklchToHex, never clipped
    // per-channel (which is what would shift the hue).
    C[s.stop] = anchorChroma > 0 ? (src.c * s.c) / anchorChroma : s.c;
  }

  // Step 2: AA enforcement on the load-bearing stops (lowers L only → safe).
  L[opts.textStop] = lowerLForTextContrast(L[opts.textStop], C[opts.textStop], src.h, SURFACE_2, 4.5);
  if (opts.bgStop && opts.bgText) {
    L[opts.bgStop] = lowerLForBgContrast(L[opts.bgStop], C[opts.bgStop], src.h, opts.bgText, 4.5);
  }
  if (opts.pairStop && opts.pairLightStop) {
    const light = oklchToHex({ l: L[opts.pairLightStop], c: C[opts.pairLightStop], h: src.h });
    L[opts.pairStop] = lowerLForTextContrast(L[opts.pairStop], C[opts.pairStop], src.h, light, 4.5);
  }

  // Step 3: clamp strictly monotonic descending (enforcement only ever lowers,
  // so a later stop can never need raising — only the inversion needs fixing).
  for (let i = 1; i < RAMP_STOPS.length; i++) {
    const cur = RAMP_STOPS[i]!;
    const prev = RAMP_STOPS[i - 1]!;
    if (L[cur] >= L[prev]) L[cur] = L[prev] - 0.012;
  }

  // Step 4: build hex.
  const ramp = {} as Ramp;
  for (const stop of RAMP_STOPS) {
    ramp[stop] = oklchToHex({ l: Math.max(L[stop], 0), c: C[stop], h: src.h });
  }
  return ramp;
}

export function deriveTealRamp(primaryHex: string): Ramp {
  return deriveRamp(primaryHex, TEAL_SKELETON, {
    // teal-600 is --color-brand-primary: the stop the firm's own colour lands on.
    anchorStop: 600,
    textStop: 600,
    bgStop: 700,
    bgText: onPrimaryFor(hexToOklch(primaryHex)),
  });
}

export function deriveBrassRamp(accentHex: string): Ramp {
  return deriveRamp(accentHex, BRASS_SKELETON, {
    // brass-400 is --color-brand-accent: the accent ramp anchors one stop lighter
    // than the primary ramp does, matching index.css.
    anchorStop: 400,
    textStop: 600,
    pairStop: 700,
    pairLightStop: 100,
  });
}

export function deriveBrandRamps(primaryHex: string, accentHex: string): DerivedRamps {
  const teal = deriveTealRamp(primaryHex);
  const brass = deriveBrassRamp(accentHex);
  return {
    teal,
    brass,
    brand: {
      primary: teal[600],
      primarySoft: teal[100],
      primaryDeep: teal[700],
      accent: brass[400],
      accentSoft: brass[100],
      onPrimary: onPrimaryFor(hexToOklch(primaryHex)),
    },
  };
}

// ── DOM injection ─────────────────────────────────────────────────────────────

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
/** Accepts #rgb / #rrggbb (case-insensitive); returns a normalised #rrggbb or null. */
export function normaliseHex(value: string | null | undefined): string | null {
  if (!value) return null;
  const v = value.trim();
  if (HEX_RE.test(v)) return v;
  const short = /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/.exec(v);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  return null;
}

/**
 * Override the brand CSS variables on `root` so the WHOLE app renders in the
 * firm's colours. Overrides the REAL consumed tokens (`--color-primary-*` /
 * `--color-accent-*` / `--color-brand-*`) — not just the orphan `--brand-*`
 * vars the old injector set — and keeps `--brand-primary`/`--brand-accent` set
 * so the ~50 surfaces that read those directly keep working.
 *
 * ## ⚠ `--brand-primary` / `--brand-accent` NOW CARRY THE DERIVED VALUE
 * (changed 2026-08-23)
 *
 * They used to carry the firm's RAW input hex, and the app therefore ran TWO
 * brand colour systems side by side:
 *
 *   - the raw pair, read by every `/auth` screen, the whole structure builder,
 *     the resolution draft preview and the onboarding chrome — roughly fifty
 *     call sites of the form `var(--brand-primary, var(--primary-600))`;
 *   - the derived, contrast-checked ramps, read by every design-system class.
 *
 * The two only agree when the firm happens to pick a colour that already sits at
 * the reference ramp's lightness. Everywhere else the same firm saw two versions
 * of its own brand on one screen, and the raw pair carried NO contrast guarantee
 * at all — a pale brand rendered unreadable link text on cream, because the raw
 * hex bypasses the AA pass by construction. It also made the ramp bug above look
 * like a mounting bug: the screens on the raw pair showed a correct red while
 * every screen on the ramps showed brown, which reads as "branding works here and
 * not there" rather than "the maths is wrong".
 *
 * Both names now resolve to the same values as `--color-brand-primary` /
 * `--color-brand-accent`. One brand, one contrast guarantee, and the fifty call
 * sites did not have to be touched to get it.
 */
export function applyBrandingToRoot(root: HTMLElement, branding: BrandingColours): void {
  const primary = normaliseHex(branding.primaryColor);
  const accent = normaliseHex(branding.accentColor);

  if (primary) {
    const teal = deriveTealRamp(primary);
    for (const stop of RAMP_STOPS) root.style.setProperty(`--color-primary-${stop}`, teal[stop]);
    root.style.setProperty('--color-brand-primary', teal[600]);
    root.style.setProperty('--color-brand-primary-soft', teal[100]);
    root.style.setProperty('--color-brand-primary-deep', teal[700]);
    root.style.setProperty('--color-brand-on-primary', onPrimaryFor(hexToOklch(primary)));
    // Same value as --color-brand-primary, on purpose — see the docstring.
    root.style.setProperty('--brand-primary', teal[600]);
  }

  if (accent) {
    const brass = deriveBrassRamp(accent);
    for (const stop of RAMP_STOPS) root.style.setProperty(`--color-accent-${stop}`, brass[stop]);
    root.style.setProperty('--color-brand-accent', brass[400]);
    root.style.setProperty('--color-brand-accent-soft', brass[100]);
    root.style.setProperty('--brand-accent', brass[400]);
  }

  // Typography — apply the firm's chosen font pair to the consumed font vars.
  // Unknown / absent → leave the baked Elreign default untouched.
  const pair = branding.fontPair ? FONT_MAP[branding.fontPair as keyof typeof FONT_MAP] : undefined;
  if (pair) {
    for (const v of HEADING_FONT_VARS) root.style.setProperty(v, pair.headingStack);
    for (const v of BODY_FONT_VARS) root.style.setProperty(v, pair.bodyStack);
  }
}

/** Remove every override `applyBrandingToRoot` sets → fall back cleanly to the
 *  baked Elreign defaults from index.css @theme. */
export function clearBrandingFromRoot(root: HTMLElement): void {
  for (const stop of RAMP_STOPS) {
    root.style.removeProperty(`--color-primary-${stop}`);
    root.style.removeProperty(`--color-accent-${stop}`);
  }
  for (const v of [
    '--color-brand-primary',
    '--color-brand-primary-soft',
    '--color-brand-primary-deep',
    '--color-brand-on-primary',
    '--color-brand-accent',
    '--color-brand-accent-soft',
    '--brand-primary',
    '--brand-accent',
    ...HEADING_FONT_VARS,
    ...BODY_FONT_VARS,
  ]) {
    root.style.removeProperty(v);
  }
}

// ── Synchronous boot cache (kills flash on repeat visits) ─────────────────────

const CACHE_PREFIX = 'elreign-branding:';

/** Cache (or evict, when payload is null) the branding colours for a key
 *  (slug or tenant id). Never throws — storage may be unavailable. */
export function cacheBranding(key: string, branding: BrandingColours | null): void {
  try {
    if (branding === null) localStorage.removeItem(CACHE_PREFIX + key);
    else localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(branding));
  } catch {
    /* private mode / quota / no storage — caching is best-effort */
  }
}

/** Read cached branding colours for a key. Returns null on miss / corrupt
 *  entry / unavailable storage. */
export function readCachedBranding(key: string): BrandingColours | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BrandingColours;
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
}
